import { useTranslation } from 'react-i18next';
import { PolicyKind, type ApprovalStatus } from '@/lib/shared-types';
import { useApi } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { countdown, formatDateTime, personName, trimDecimal } from './lib';

export interface Person {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

export interface PolicyApproval {
  id: string;
  branchId: string | null;
  policy: PolicyKind;
  status: ApprovalStatus;
  entityType: string;
  entityId: string | null;
  /** Prisma `Decimal` — a string. Never parsed to a float for display. */
  amount: string | null;
  reason: string;
  payload: Record<string, unknown> | null;
  requestedById: string;
  resolutionNote: string | null;
  requestedAt: string;
  resolvedAt: string | null;
  expiresAt: string | null;
  requestedBy: Person | null;
  resolvedBy: Person | null;
  branch: { id: string; name: string } | null;
}

/** Fields of `payload` worth showing per policy, in the order they read best. */
export const PAYLOAD_FIELDS: Record<PolicyKind, string[]> = {
  DISCOUNT: ['requestReason', 'baseAmount', 'percent', 'checkId'],
  VOID: ['orderNumber', 'orderStatus', 'requestReason'],
  REFUND: ['mode', 'paymentStatus', 'requestReason', 'paymentId'],
  PRICE_OVERRIDE: ['itemName', 'catalogPrice', 'unitPrice', 'requestReason'],
  STOCK_ADJUSTMENT: ['itemName', 'sku', 'quantity', 'requestReason'],
};

/** Quantities keep their trailing-zero noise off the screen; the rest is text. */
export function payloadValue(field: string, raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  if (field === 'quantity') return trimDecimal(raw);
  if (field === 'percent') return `${String(raw)}%`;
  return String(raw);
}

/**
 * Money for one approval, formatted in **its own branch's** currency — a chain
 * with a branch abroad must not have that branch's refunds priced in the
 * head-office currency. `useCurrency` is a hook, so this is a component rather
 * than a helper called per row.
 */
export function Amount({ amount, branchId }: { amount: string | null; branchId: string | null }) {
  const { format } = useCurrency(branchId);
  if (amount === null) return <span className="text-ink-muted">—</span>;
  return <span className="font-medium tabular-nums">{format(amount)}</span>;
}

/* ------------------------------------------------------------- order lines */

interface OrderLine {
  id: string;
  name: string;
  quantity: string;
  unitPrice: string;
  total: string;
}

/**
 * `Items Affected by Void` — the frame's second card, and the one piece of it
 * that is not in the request.
 *
 * `VoidApprovalPayload` carries `policy, orderId, orderNumber, orderStatus,
 * amount, requestReason, operationId` and **no line items**, so the table is a
 * second fetch of `GET /orders/:id` on `orders.view` — a different key from the
 * `approvals.view` that opened this screen, which is why it says so rather than
 * rendering an empty card.
 *
 * The gate's own comment is worth heeding: the parked status and total *"are the
 * ones the gate measured, so an approver sees what they are signing even if the
 * order has moved on"*. So the live order can legitimately disagree with
 * `Affected amount`, and the hint below labels which number is which instead of
 * quietly showing one.
 */
function AffectedItems({ approval }: { approval: PolicyApproval }) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { format } = useCurrency(approval.branchId);

  const orderId =
    typeof approval.payload?.orderId === 'string'
      ? approval.payload.orderId
      : approval.entityType === 'Order'
        ? approval.entityId
        : null;
  const mayRead = can('orders.view');

  const order = useApi<{ items?: OrderLine[]; total?: string }>(
    mayRead && orderId ? `/orders/${orderId}` : null,
  );

  if (!orderId) return null;

  return (
    <section className="rounded-lg border p-4">
      <h3 className="mb-3 text-sm font-semibold">
        {t('platform.approvals.affectedItems')}
      </h3>

      {!mayRead ? (
        <p className="text-sm text-ink-muted">
          {t('platform.approvals.itemsNotPermitted')}
        </p>
      ) : order.loading ? (
        <LoadingState label={t('common.loading')} />
      ) : order.error ? (
        <ErrorState message={order.error} onRetry={order.reload} />
      ) : (
        <>
          <TableWrap className="border-0">
            <Table>
              <THead>
                <TR>
                  <TH>{t('operations.orders.item')}</TH>
                  <TH className="text-end">{t('common.quantity')}</TH>
                  <TH className="text-end">
                    {t('platform.approvals.unitPrice')}
                  </TH>
                  <TH className="text-end">{t('common.total')}</TH>
                </TR>
              </THead>
              <tbody>
                {(order.data?.items ?? []).length === 0 && (
                  <TR>
                    <TD colSpan={4} className="text-ink-muted">
                      {t('platform.approvals.noItems')}
                    </TD>
                  </TR>
                )}
                {(order.data?.items ?? []).map((line) => (
                  <TR key={line.id}>
                    <TD>{line.name}</TD>
                    <TD className="text-end tabular-nums">{trimDecimal(line.quantity)}</TD>
                    <TD className="text-end tabular-nums">{format(line.unitPrice)}</TD>
                    <TD className="text-end tabular-nums">{format(line.total)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
          <p className="mt-2 text-xs text-ink-muted">
            {t('platform.approvals.affectedItemsHint', {
              amount: approval.amount === null ? '—' : format(approval.amount),
            })}
          </p>
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ detail */

/**
 * Frame `64:2855` — one §24.5 request, in full.
 *
 * Three of the frame's regions are not built, each because the model does not
 * carry them:
 *
 *  - **`Approval Workflow Route` (Step 1/2/3)** is invented. `PolicyApproval` is
 *    single-step — `requestedById` → `resolvedById`, plus `status`, `resolvedAt`,
 *    `resolutionNote`, `expiresAt`. Nothing escalates to a Floor Manager. What is
 *    below is that two-event history, with the expiry as the pending deadline.
 *  - **`Tarabeeza Policy Alert`** states a threshold rule that is wrong (every
 *    threshold is *strictly above*, and the gate parks a request for **anyone**
 *    holding the right key rather than escalating) and could not be rendered
 *    anyway: thresholds live in tenant/branch `settings` JSON with no read
 *    endpoint. `PolicyApproval.reason` is the policy's own words for why a
 *    signature is required, and is the dialog's description.
 *  - **`APR-260821-002`** is not a reference this product issues; `id` is a
 *    cuid. The reference shown is `entityType` + `entityId`, which is real.
 *
 * The authority model the frame leaves out is the substance of the screen:
 * answering needs `approvals.approve` **or** the per-kind key, rejecting is
 * gated identically and deliberately, self-approval is refused without
 * `approvals.approve_own`, and a request lapses after about a shift. Each of
 * those is stated where the buttons are rather than left to a 403.
 */
export function ApprovalDetail({
  approval,
  now,
  maySign,
  isOwn,
  onDecide,
  onClose,
}: {
  approval: PolicyApproval | null;
  /** Ticking clock from the queue, so the countdown here and there agree. */
  now: number;
  maySign: boolean;
  isOwn: boolean;
  onDecide: (kind: 'approve' | 'reject', approval: PolicyApproval) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();

  if (!approval) return null;

  const left = countdown(approval.expiresAt, now);
  const lapsed = approval.status === 'PENDING' && left?.expired;
  const actionable = approval.status === 'PENDING' && !lapsed && maySign && !isOwn;

  const facts: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: t('platform.approvals.reference'),
      value: (
        <span dir="ltr">
          {approval.entityType}
          {approval.entityId ? ` · ${approval.entityId}` : ''}
        </span>
      ),
    },
    {
      label: t('platform.approvals.amount'),
      value: <Amount amount={approval.amount} branchId={approval.branchId} />,
    },
    {
      label: t('platform.approvals.requester'),
      value: `${personName(approval.requestedBy)}${isOwn ? ` (${t('platform.approvals.you')})` : ''}`,
    },
    {
      label: t('platform.approvals.requested'),
      value: formatDateTime(approval.requestedAt, i18n.language),
    },
    ...(approval.branch
      ? [
          {
            label: t('platform.approvals.branch'),
            value: approval.branch.name,
          },
        ]
      : []),
    ...(PAYLOAD_FIELDS[approval.policy] ?? [])
      .filter((field) => approval.payload?.[field] != null)
      .map((field) => ({
        label: t(`platform.approvals.payload.${field}`),
        // `orderStatus` is an `OrderStatus`, so it gets the tone the table
        // assigns it and the label the app already ships — never the raw member,
        // and never a hand-picked colour (the frame's `Recalled & Canceled` is
        // not a member of that enum at all).
        value:
          field === 'orderStatus' ? (
            <Badge tone={toneForStatus(String(approval.payload?.[field]) as never)}>
              {t(`operations.orderStatus.${String(approval.payload?.[field])}`, {
                defaultValue: String(approval.payload?.[field]),
              })}
            </Badge>
          ) : (
            payloadValue(field, approval.payload?.[field])
          ),
      })),
  ];

  /** Every event this model can actually prove happened. */
  const timeline: Array<{ at: string; label: string; detail?: string }> = [
    {
      at: approval.requestedAt,
      label: t('platform.approvals.requested'),
      detail: personName(approval.requestedBy),
    },
    ...(approval.resolvedAt
      ? [
          {
            at: approval.resolvedAt,
            label: t(`platform.approvals.statusLabel.${approval.status}`),
            detail: personName(approval.resolvedBy),
          },
        ]
      : []),
  ];

  /** The one sentence that explains why the buttons are as they are. */
  const standing = (): string | null => {
    if (approval.status !== 'PENDING') return t('platform.approvals.alreadyAnswered');
    if (lapsed) return t('platform.approvals.lapsedHint');
    if (!maySign) return t('platform.approvals.notPermitted');
    if (isOwn) return t('platform.approvals.selfBlocked');
    return null;
  };
  const note = standing();

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(`platform.approvals.policyLabel.${approval.policy}`)}
      description={approval.reason}
      className="max-w-4xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button
            variant="danger"
            disabled={!actionable}
            title={note ?? undefined}
            onClick={() => onDecide('reject', approval)}
          >
            {t('platform.approvals.reject')}
          </Button>
          <Button
            disabled={!actionable}
            title={note ?? undefined}
            onClick={() => onDecide('approve', approval)}
          >
            {t('platform.approvals.approve')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={toneForStatus(approval.status)}>
              {t(`platform.approvals.statusLabel.${approval.status}`)}
            </Badge>
            {approval.status === 'PENDING' &&
              (!left ? (
                <span className="text-sm text-ink-muted">
                  {t('platform.approvals.noExpiry')}
                </span>
              ) : left.expired ? (
                <Badge tone="danger">{t('platform.approvals.lapsed')}</Badge>
              ) : (
                <span
                  className={
                    left.urgent
                      ? 'text-sm font-medium tabular-nums text-red-600 dark:text-red-400'
                      : 'text-sm tabular-nums text-ink-muted'
                  }
                >
                  {t('platform.approvals.expiresIn')} {left.label}
                </span>
              ))}
          </div>

          <section className="rounded-lg border p-4">
            <h3 className="mb-3 text-sm font-semibold">{t('platform.approvals.request')}</h3>
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {facts.map((fact) => (
                <div key={fact.label}>
                  <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{fact.label}</dt>
                  <dd className="mt-0.5 text-sm">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {approval.policy === PolicyKind.VOID && <AffectedItems approval={approval} />}
        </div>

        <aside className="space-y-4">
          <section className="rounded-lg border p-4">
            <h3 className="mb-3 text-sm font-semibold">
              {t('platform.approvals.timeline')}
            </h3>
            <ol className="space-y-3">
              {timeline.map((entry) => (
                <li key={entry.label} className="text-sm">
                  <span className="block text-ink-muted tabular-nums">
                    {formatDateTime(entry.at, i18n.language)}
                  </span>
                  <span className="block">{entry.label}</span>
                  {entry.detail && (
                    <span className="block text-xs text-ink-muted">{entry.detail}</span>
                  )}
                </li>
              ))}
            </ol>
            {approval.resolutionNote && (
              <p className="mt-3 border-t pt-3 text-sm italic text-ink-muted">
                “{approval.resolutionNote}”
              </p>
            )}
          </section>

          {note && (
            <p className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3 text-sm text-amber-900 dark:text-amber-200">
              {note}
            </p>
          )}
        </aside>
      </div>
    </Dialog>
  );
}
